import {
  findNearMatches,
  type AreaReportIndexEntry,
  type WineType,
} from "@corklog/shared";
import { useEffect, useState } from "react";

import { fetchAreaReportIndex } from "./api.js";
import { producerKey } from "./area-match.js";
import { useMayDiscover } from "./session.js";

/**
 * Links from what the cellar records to the area report that describes it —
 * task 10.36, the second way in that `project-scope.md` named on 2026-09-28:
 * *Upptäck*'s tree, and a link from a region or a producer. There is no page
 * of its own for either, so the links stand where they are seen: a wine's
 * page, under the line naming its producer and region, and each house in
 * *Producenter*.
 *
 * **Matched by the rules the rest of the application uses to say two names
 * are one** — case, spaces and accents, or `findNearMatches`' same-name rule —
 * never by the looser similarity, since a link is a claim that the report is
 * about this.
 */

/** A report a link leads to. */
export type AreaLink = { readonly id: string; readonly area: string };

const same = (a: string, b: string): boolean =>
  producerKey(a) === producerKey(b) ||
  findNearMatches(a, [b]).some((near) => near.reason === "same-name-different-spelling");

/**
 * The report that names this house — as a producer, or through one of its
 * cuvées, since a cellar may have registered *Dom Pérignon* where the report
 * says *Moët & Chandon*. The first report that does, in the index's order.
 */
export function reportForProducer(
  index: readonly AreaReportIndexEntry[],
  producer: string,
): AreaLink | null {
  for (const report of index) {
    const named = report.producers.some(
      (house) =>
        same(house.name, producer) || house.cuvees.some((c) => same(c, producer)),
    );
    if (named) return { id: report.id, area: report.area };
  }
  return null;
}

/**
 * The report about this region: one whose area is the region — *Champagne* —
 * or whose regions include it — *Côte des Blancs* — in the same country.
 * **Of those, one about the wine's own kind first**: a Loire Crémant leads to
 * the Crémant report, not the one about the Loire's white wines.
 */
export function reportForRegion(
  index: readonly AreaReportIndexEntry[],
  countryCode: string,
  region: string,
  kind: WineType,
): AreaLink | null {
  const matches = index.filter(
    (report) =>
      report.countryCode === countryCode &&
      (same(report.area, region) || report.regions.some((name) => same(name, region))),
  );
  const best = matches.find((report) => report.kind === kind) ?? matches[0];
  return best === undefined ? null : { id: best.id, area: best.area };
}

let cached: Promise<AreaReportIndexEntry[]> | null = null;

/**
 * The index, fetched once per page load and shared by every page that asks —
 * a wine's page is opened often, and the reports change a few times a month.
 * **Null for a reader who is not shown _Upptäck_**, without asking: the route
 * would refuse, and a link to a page that refuses is worse than none.
 */
export function useAreaIndex(): readonly AreaReportIndexEntry[] | null {
  const allowed = useMayDiscover();
  const [index, setIndex] = useState<readonly AreaReportIndexEntry[] | null>(null);

  useEffect(() => {
    if (!allowed) {
      setIndex(null);
      return;
    }
    let current = true;
    cached ??= fetchAreaReportIndex()
      .then((body) => body.reports)
      .catch(() => {
        // A failure is not remembered: the next page tries again.
        cached = null;
        return [];
      });
    void cached.then((reports) => {
      if (current) setIndex(reports);
    });
    return () => {
      current = false;
    };
  }, [allowed]);

  return index;
}
